import { useEffect, useRef } from "react";
import { useApp } from "@/context/AppContext";
import { ASSETS, postEvent } from "@/lib/site";

export function WelcomeGate() {
  const { t, gateOpen, setGuide, setGateOpen } = useApp();
  const ref = useRef(null);

  useEffect(() => {
    if (!gateOpen) return;
    const onKey = (e) => e.key === "Escape" && setGateOpen(false);
    window.addEventListener("keydown", onKey);
    ref.current?.querySelector("button")?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [gateOpen, setGateOpen]);

  if (!gateOpen) return null;

  const choose = (g) => {
    setGuide(g);
    postEvent("guide_selected", { guide: g || "none" });
  };

  const cards = [
    {
      id: "zeta",
      name: "ZETA",
      color: "#2ECBD9",
      img: ASSETS.zetaChar,
      desc: { tr: "Sakin ve detaycı bir rehber. Mavi yol sizi adım adım götürür.", en: "A calm, detail-focused guide. The blue path takes you step by step." },
    },
    {
      id: "atez",
      name: "ATEZ",
      color: "#A8D93B",
      img: ASSETS.atezChar,
      desc: { tr: "Enerjik ve pratik bir rehber. Yeşil yol sizi hızlıca yönlendirir.", en: "An energetic, practical guide. The green path directs you swiftly." },
    },
  ];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t({ tr: "Rehber seçimi", en: "Guide selection" })}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
      data-testid="welcome-gate"
      ref={ref}
    >
      <div className="absolute inset-0 bg-[#05070A]/80 backdrop-blur-md" onClick={() => setGateOpen(false)} />
      <div className="relative w-full max-w-3xl rounded-2xl gate-motif border border-white/10 shadow-2xl overflow-hidden my-8">
        <div className="relative px-6 md:px-12 pt-10 pb-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#F2F5F7]" data-testid="gate-headline">
            {t({ tr: "Size bu yolculukta kim eşlik etsin?", en: "Who should accompany you on this journey?" })}
          </h2>
          <p className="mt-2 text-sm text-[#94A3AE]" data-testid="gate-subline">
            {t({ tr: "İstediğiniz zaman değiştirebilirsiniz. Her iki rehber de aynı hizmetleri sunar.", en: "You can change anytime. Both guides offer the same services." })}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
            {cards.map((c) => (
              <button
                key={c.id}
                data-testid={`gate-card-${c.id}`}
                onClick={() => choose(c.id)}
                className="group relative glass-card p-5 text-left transition-transform hover:-translate-y-1 focus:outline-none focus:ring-2"
                style={{ "--tw-ring-color": c.color }}
              >
                <div
                  className="h-44 rounded-xl overflow-hidden mb-4 flex items-end justify-center transition-transform duration-500 group-hover:scale-[1.03]"
                  style={{ background: `radial-gradient(ellipse at 50% 35%, ${c.color}1f, transparent 72%)` }}
                >
                  <img src={c.img} alt={c.name} className="h-40 w-auto object-contain" loading="eager" />
                </div>
                <span className="font-heading text-lg font-bold tracking-wide" style={{ color: c.color }}>
                  {c.name}
                </span>
                <p className="text-xs text-[#94A3AE] mt-1.5 leading-relaxed">{t(c.desc)}</p>
                <span className="mt-3 inline-block rounded-[10px] px-4 py-2 text-xs font-semibold text-[#05070A]" style={{ backgroundColor: c.color }}>
                  {t({ tr: `${c.name} ile devam et`, en: `Continue with ${c.name}` })}
                </span>
              </button>
            ))}
          </div>

          <button
            data-testid="gate-skip"
            onClick={() => choose(null)}
            className="mt-6 text-sm text-[#94A3AE] underline underline-offset-4 hover:text-[#F2F5F7] transition-colors"
          >
            {t({ tr: "Rehbersiz devam et", en: "Continue without a guide" })}
          </button>
        </div>
      </div>
    </div>
  );
}
